import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

export interface CognitoSignUpResult {
  userSub: string;
  userConfirmed: boolean;
}

export interface CognitoSignupProfile {
  city: string;
  lang: string;
}

const toCognitoLang = (lang: string): string =>
  (lang || "fr").toLowerCase().split("-")[0];

// --- Cognito Configuration ---
const userPoolId = process.env.REACT_APP_USERPOOL_ID!;
const clientId = process.env.REACT_APP_CLIENT_ID!;

const userPool = new CognitoUserPool({
  UserPoolId: userPoolId,
  ClientId: clientId,
});

/**
 * Creates a CognitoUser instance for the given username.
 * @param username - The username (usually email) of the user.
 * @returns A CognitoUser object bound to the pool.
 */
const getCognitoUser = (username: string): CognitoUser =>
  new CognitoUser({ Username: username, Pool: userPool });

/**
 * Retrieves the currently logged-in Cognito user (from local storage/session).
 * @returns The current CognitoUser instance or null if none found.
 */
export const getCurrentUser = (): CognitoUser | null =>
  userPool.getCurrentUser();

/**
 * Retrieves the current authenticated session for the user.
 * @returns A Promise resolving with the CognitoUserSession object.
 * @throws Error if no user session exists or if session retrieval fails.
 */
export const getSession = async (): Promise<CognitoUserSession> => {
  const user = getCurrentUser();
  console.log("getSession: current user:", user);
  if (!user) throw new Error("No user session found");

  return new Promise((resolve, reject) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) return reject(err);
      resolve(session);
    });
  });
};

/**
 * Checks if the user's session is still valid; if not, logs out.
 * Intended to run before protected API calls.
 */
export const validateOrLogout = async (): Promise<string | null> => {
  try {
    const session = await ensureValidSession();
    return session.getIdToken().getJwtToken();
  } catch (error) {
    console.warn("Session expired — signing out user");
    await signOut();
    return null;
  }
};

/**
 * Ensures the current user has a valid session.
 * If the access token is expired but the refresh token is still valid,
 * Cognito will automatically refresh the session.
 *
 * @returns Promise resolving with a valid CognitoUserSession.
 * @throws Error if the session cannot be refreshed (refresh token expired or no user).
 */
export const ensureValidSession = async (): Promise<CognitoUserSession> => {
  const user = userPool.getCurrentUser();
  if (!user) throw new Error("No current user found");

  return new Promise((resolve, reject) => {
    user.getSession(
      async (err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) return reject(err);

        // If access token expired but refresh token still valid:
        if (!session.isValid()) {
          user.refreshSession(
            session.getRefreshToken(),
            (refreshErr, refreshedSession) => {
              if (refreshErr) return reject(refreshErr);
              resolve(refreshedSession);
            },
          );
        } else {
          resolve(session);
        }
      },
    );
  });
};

const getAuthenticatedUser = async (): Promise<CognitoUser> => {
  const user = getCurrentUser();
  if (!user) {
    throw new Error("No authenticated user");
  }

  await new Promise<void>((resolve, reject) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        reject(err ?? new Error("No active session"));
        return;
      }

      if (!session.isValid()) {
        user.refreshSession(session.getRefreshToken(), (refreshErr) => {
          if (refreshErr) {
            reject(refreshErr);
            return;
          }
          resolve();
        });
        return;
      }

      resolve();
    });
  });

  return user;
};

/**
 * Signs up a new user with username, email, and password.
 * Optionally allows specifying a userId (sub).
 * @param username - Display name for the user.
 * @param email - User's email (used as username in Cognito).
 * @param password - Password for the account.
 * @param userId - Optional unique user identifier (Cognito 'sub').
 * @returns Promise resolving with the Cognito sign-up result.
 */
export const signUpUser = async (
  username: string,
  email: string,
  password: string,
  phoneNumber: string,
  givenName: string,
  familyName: string,
  profile: CognitoSignupProfile,
): Promise<CognitoSignUpResult> => {
  const attributes = [
    new CognitoUserAttribute({ Name: "name", Value: username }),
    new CognitoUserAttribute({ Name: "email", Value: email }),
    new CognitoUserAttribute({ Name: "phone_number", Value: phoneNumber }),
    new CognitoUserAttribute({ Name: "given_name", Value: givenName }),
    new CognitoUserAttribute({ Name: "family_name", Value: familyName }),
    new CognitoUserAttribute({ Name: "zoneinfo", Value: profile.city }),
    new CognitoUserAttribute({
      Name: "locale",
      Value: toCognitoLang(profile.lang),
    }),
  ];

  const clientMetadata = {
    lang: toCognitoLang(profile.lang),
  };

  return new Promise((resolve, reject) => {
    (userPool as any).signUp(
      email,
      password,
      attributes,
      [],
      (err: Error | null, res: any) => {
        if (err || !res) {
          reject(err ?? new Error("Sign up failed"));
          return;
        }

        resolve({
          userSub: res.userSub,
          userConfirmed: res.userConfirmed,
        });
      },
      clientMetadata,
    );
  });
};

/**
 * Resends a sign-up verification code for an unconfirmed user.
 * @param username - The user's email.
 */
export const resendVerificationCode = async (username: string) => {
  const user = getCognitoUser(username);
  return new Promise<void>((resolve, reject) => {
    user.resendConfirmationCode((err) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
};

/**
 * Confirms user registration using the verification code from email.
 * @param username - The username/email of the user.
 * @param code - The verification code sent to the user's email.
 * @returns Promise resolving with the confirmation result.
 */
export const verifyCode = async (username: string, code: string) => {
  const user = getCognitoUser(username);
  return new Promise((resolve, reject) => {
    user.confirmRegistration(code, true, (err, result) =>
      err ? reject(err) : resolve(result),
    );
  });
};

/**
 * Authenticates a user with email and password.
 * @param username - The user's email.
 * @param password - The user's password.
 * @returns Promise resolving with authentication result and tokens.
 */
export const signIn = async (
  username: string,
  password: string,
): Promise<CognitoUserSession> => {
  const authDetails = new AuthenticationDetails({
    Username: username,
    Password: password,
  });
  const user = getCognitoUser(username);

  return new Promise((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess: (session) => resolve(session),
      onFailure: reject,
    });
  });
};

/**
 * Signs out the currently authenticated user globally (all devices).
 * @returns Promise that resolves when sign-out completes.
 */
export const signOut = async (): Promise<void> => {
  const user = getCurrentUser();
  if (!user) return;

  return new Promise((resolve, reject) => {
    user.globalSignOut({
      onSuccess: () => resolve(),
      onFailure: (err) => {
        const message = err?.message?.toLowerCase?.() ?? "";
        const errWithCode = err as Error & { code?: string };
        const cognitoCode = (errWithCode.code ?? "").toLowerCase();
        const isAlreadySignedOut =
          message.includes("not authenticated") ||
          message.includes("access token") ||
          cognitoCode.includes("notauthorized");

        if (isAlreadySignedOut) {
          user.signOut();
          resolve();
          return;
        }

        reject(err);
      },
    });
  });
};

/**
 * Sends a password reset (forgot password) code to the user’s email.
 * @param username - The user’s email or username.
 * @returns Promise resolving when the reset code is sent.
 */
export const sendResetCode = async (username: string, lang: string) => {
  const user = getCognitoUser(username);
  return new Promise((resolve, reject) => {
    (user as any).forgotPassword({
      ClientMetadata: {
        lang: toCognitoLang(lang),
      },
      onSuccess: resolve,
      onFailure: reject,
    });
  });
};

/**
 * Confirms a new password using the verification code received via email.
 * @param username - The user's email.
 * @param code - The verification code.
 * @param newPassword - The new password to set.
 * @returns Promise resolving with a success message.
 */
export const confirmResetPassword = async (
  username: string,
  code: string,
  newPassword: string,
) => {
  const user = getCognitoUser(username);
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve("Password successfully updated"),
      onFailure: reject,
    });
  });
};

/**
 * Changes the current user's password.
 * @param oldPassword - The user's current password.
 * @param newPassword - The new password to set.
 * @returns Promise resolving when password is successfully changed.
 * @throws Error if no authenticated user exists.
 */
export const changePassword = async (
  oldPassword: string,
  newPassword: string,
) => {
  const user = await getAuthenticatedUser();

  return new Promise((resolve, reject) => {
    user.changePassword(oldPassword, newPassword, (err, res) =>
      err ? reject(err) : resolve(res),
    );
  });
};

/**
 * Retrieves all attributes (name, email, etc.) of the authenticated user.
 * @returns Promise resolving with a list of CognitoUserAttributes.
 * @throws Error if no authenticated user exists.
 */
export const getAttributes = async () => {
  const user = await getAuthenticatedUser();

  return new Promise((resolve, reject) => {
    user.getUserAttributes((err, attributes) =>
      err ? reject(err) : resolve(attributes),
    );
  });
};

/**
 * Updates user attributes (e.g., name, custom fields) for the authenticated user.
 * @param attributes - An object where keys are attribute names and values are their values.
 * @returns Promise resolving when attributes are successfully updated.
 * @throws Error if no authenticated user exists.
 */
export const updateAttributes = async (attributes: Record<string, string>) => {
  const user = await getAuthenticatedUser();

  const attributeList = Object.entries(attributes).map(
    ([key, value]) => new CognitoUserAttribute({ Name: key, Value: value }),
  );

  return new Promise((resolve, reject) => {
    user.updateAttributes(attributeList, (err, res) =>
      err ? reject(err) : resolve(res),
    );
  });
};
